import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount } from "wagmi";
import {
  FaUsers,
  FaUserPlus,
  FaUserCheck,
  FaUserTimes,
  FaCalendarWeek,
  FaCalendarAlt,
  FaCalendarDay,
  FaMoneyBillWave,
  FaExchangeAlt,
  FaChartLine,
  FaGift,
  FaBuilding,
  FaHandHoldingUsd,
  FaWallet,
  FaShieldAlt,
  FaAngleRight,
  FaCrown
} from "react-icons/fa";
import { GiReceiveMoney, GiPayMoney, GiMoneyStack } from "react-icons/gi";
import { MdOutlineSavings, MdShowChart } from "react-icons/md";
import { RiCoinsFill } from "react-icons/ri";
import { BsGraphUpArrow } from "react-icons/bs";
import { TbBusinessplan } from "react-icons/tb";
import {
  getDashboardData,
} from "../../../../services/api_function";
import { getOwnerAddress } from "../../../../services/getWeb3";

// Add CSS for animations in your main CSS file or as inline styles
const shimmerAnimation = `
@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 0.8; }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.card {
  animation: fadeIn 0.3s ease-out;
}

.stat-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}
`;

const BalanceCardSlider = () => {
  const [data, setData] = useState(null);
  const [Admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { address } = useAccount();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const owner = await getOwnerAddress();
        setAdmin(owner);
        
        const res = await getDashboardData();
        
        if (res?.status === 200) {
          setData(res?.data?.data);
        } else if (res?.status === 404) {
          localStorage.removeItem("adminToken");
          navigate("/login");
        } else {
          setError("Failed to load dashboard data");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("An error occurred while loading dashboard data");
      } finally {
        // Add a small delay for better UX
        setTimeout(() => {
          setLoading(false);
        }, 800);
      }
    };
    fetchData();
  }, [navigate]);

  // Enhanced Loading Component
  if (loading) {
    return (
      <>
        <style>{shimmerAnimation}</style>
        <div className="container-fluid">
          <h3 className="mb-4 text-dark">Dashboard Statistics</h3>
          <div className="row">
            {[...Array(18)].map((_, index) => (
              <div className="col-sm-6 col-xl-3 mb-4" key={index}>
                <div 
                  className="card stat-card shadow-sm border-0" 
                  style={{
                    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    position: "relative",
                    minHeight: "140px"
                  }}
                >
                  {/* Shimmer Effect */}
                  <div 
                    className="shimmer"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                      animation: "shimmer 2s infinite",
                      zIndex: 1
                    }}
                  />
                  
                  <div className="card-body p-4" style={{ position: "relative", zIndex: 2 }}>
                    <div className="d-flex align-items-center mb-3">
                      {/* Icon Skeleton */}
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "48px",
                          height: "48px",
                          background: "linear-gradient(135deg, #dee2e6 0%, #adb5bd 100%)",
                          animation: "pulse 1.5s ease-in-out infinite"
                        }}
                      />
                      
                      {/* Title Skeleton */}
                      <div style={{ flex: 1 }}>
                        <div 
                          className="placeholder-glow mb-1"
                          style={{
                            height: "10px",
                            width: "70%",
                            borderRadius: "4px",
                            background: "linear-gradient(90deg, #ced4da 25%, #e9ecef 50%, #ced4da 75%)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 1.5s infinite"
                          }}
                        />
                        <div 
                          className="placeholder-glow"
                          style={{
                            height: "8px",
                            width: "50%",
                            borderRadius: "4px",
                            background: "linear-gradient(90deg, #e9ecef 25%, #dee2e6 50%, #e9ecef 75%)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 1.5s infinite"
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Value Skeleton */}
                    <div className="mt-2">
                      <div 
                        className="placeholder-glow mb-1"
                        style={{
                          height: "20px",
                          width: "40%",
                          borderRadius: "6px",
                          background: "linear-gradient(90deg, #adb5bd 25%, #ced4da 50%, #adb5bd 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s infinite",
                          marginLeft: "auto"
                        }}
                      />
                      <div 
                        className="placeholder-glow"
                        style={{
                          height: "14px",
                          width: "30%",
                          borderRadius: "4px",
                          background: "linear-gradient(90deg, #ced4da 25%, #dee2e6 50%, #ced4da 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s infinite",
                          marginLeft: "auto"
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Error Component
  if (error) {
    return (
      <div className="container-fluid">
        <div 
          className="alert alert-danger d-flex align-items-center shadow-sm" 
          role="alert"
          style={{
            borderRadius: "12px",
            border: "none",
            background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
            color: "white"
          }}
        >
          <div className="me-3">
            <i className="bi bi-exclamation-triangle-fill fs-4"></i>
          </div>
          <div>
            <h5 className="alert-heading mb-2">Unable to Load Dashboard</h5>
            <p className="mb-3">{error}</p>
            <button 
              className="btn btn-light btn-sm shadow" 
              onClick={() => window.location.reload()}
              style={{ borderRadius: "8px" }}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{shimmerAnimation}</style>
      <div className="container-fluid">
        <h3 className="mb-4 text-dark fw-bold">Dashboard Overview</h3>
        <div className="row">
          {/* Total Users */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <FaUsers className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Total Users</h6>
                  <span className="fw-bold fs-4">{data?.totalUser || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>All registered users</small>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Users */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <FaCalendarWeek className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Weekly Users</h6>
                  <span className="fw-bold fs-4">{data?.weeklyUser || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>Last 7 days</small>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Users */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <FaCalendarAlt className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Monthly Users</h6>
                  <span className="fw-bold fs-4 text-dark">{data?.monthlyUser || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>Last 30 days</small>
                </div>
              </div>
            </div>
          </div>

          {/* Today Users */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <FaCalendarDay className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Today Users</h6>
                  <span className="fw-bold fs-4 text-dark">{data?.todayUser || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>24 hour period</small>
                </div>
              </div>
            </div>
          </div>

          {/* Active Users */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <FaUserCheck className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Active Users</h6>
                  <span className="fw-bold fs-4 text-dark">{data?.totalActiveUser || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>Currently active</small>
                </div>
              </div>
            </div>
          </div>

          {/* Inactive Users */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <FaUserTimes className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Inactive Users</h6>
                  <span className="fw-bold fs-4">{data?.totalInactiveUser || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>Not currently active</small>
                </div>
              </div>
            </div>
          </div>

          {/* Total Deposit */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <GiMoneyStack className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Total Deposit</h6>
                  <span className="fw-bold fs-4">${data?.totalDeposit?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>All time deposits</small>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Deposit */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <MdOutlineSavings className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Weekly Deposit</h6>
                  <span className="fw-bold fs-4 text-black">${data?.weeklyDeposit?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>Last 7 days</small>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Deposit */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <GiReceiveMoney className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Monthly Deposit</h6>
                  <span className="fw-bold fs-4">${data?.monthlyDeposit?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>Last 30 days</small>
                </div>
              </div>
            </div>
          </div>

          {/* Total Flush */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #ff8177 0%, #ff867a 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <FaExchangeAlt className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Total Flush</h6>
                  <span className="fw-bold fs-4">${data?.totalFlush?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>All time flush</small>
                </div>
              </div>
            </div>
          </div>

          {/* Today Deposit */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <FaWallet className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Today Deposit</h6>
                  <span className="fw-bold fs-4">${data?.todayDeposit?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>24 hour deposits</small>
                </div>
              </div>
            </div>
          </div>

          {/* Total Withdraw */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <GiPayMoney className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Total Withdraw</h6>
                  <span className="fw-bold fs-4">${data?.totalWithdrawAmount?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>All time withdrawals</small>
                </div>
              </div>
            </div>
          </div>

          {/* Total BDF Distribution */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #a6c0fe 0%, #f68084 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <RiCoinsFill className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">BDF Distribution</h6>
                  <span className="fw-bold fs-4 text-black">${data?.totalBDFIncomeAmount?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>Total distributed</small>
                </div>
              </div>
            </div>
          </div>

          {/* Total Airdrop */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <FaGift className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Total Airdrop</h6>
                  <span className="fw-bold fs-4">${data?.totalAirDropAmount?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>All airdrops</small>
                </div>
              </div>
            </div>
          </div>

          {/* Protocol-1 Business */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #cd9cf2 0%, #f6f3ff 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <TbBusinessplan className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Protocol-1 Business</h6>
                  <span className="fw-bold fs-4">${data?.totalBusinessProtocol1?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>First protocol</small>
                </div>
              </div>
            </div>
          </div>

          {/* Protocol-2 Business */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(0, 0, 0, 0.1)"
                    }}>
                    <FaBuilding className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Protocol-2 Business</h6>
                  <span className="fw-bold fs-4">${data?.totalBusinessProtocol2?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.7 }}>Second protocol</small>
                </div>
              </div>
            </div>
          </div>

          {/* Protocol-3 Business */}
          <div className="col-sm-6 col-xl-3 mb-4">
            <div 
              className="card stat-card shadow-sm border-0" 
              style={{
                background: "linear-gradient(135deg, #a3bded 0%, #6991c7 100%)",
                borderRadius: "12px",
                overflow: "hidden",
                color: "white"
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255, 255, 255, 0.2)"
                    }}>
                    <FaChartLine className="fs-4" />
                  </div>
                  <div className="ms-auto">
                    <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <h6 className="mb-0 fw-semibold">Protocol-3 Business</h6>
                  <span className="fw-bold fs-4">${data?.totalBusinessProtocol3?.toFixed(2) || 0}</span>
                </div>
                <div className="mt-2">
                  <small style={{ opacity: 0.8 }}>Third protocol</small>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Total Earn */}
          {address?.toLowerCase() === Admin?.toLowerCase() && (
            <div className="col-sm-6 col-xl-3 mb-4">
              <div 
                className="card stat-card shadow-sm border-0" 
                style={{
                  background: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  color: "#333"
                }}
              >
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: "48px",
                        height: "48px",
                        background: "rgba(0, 0, 0, 0.1)"
                      }}>
                      <FaCrown className="fs-4" />
                    </div>
                    <div className="ms-auto">
                      <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <h6 className="mb-0 fw-semibold">Admin Total Earn</h6>
                    <span className="fw-bold fs-4">${data?.FirstUserTotalIncome?.toFixed(2) || 0}</span>
                  </div>
                  <div className="mt-2">
                    <small style={{ opacity: 0.7 }}>All time earnings</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Admin Today Earn */}
          {address?.toLowerCase() === Admin?.toLowerCase() && (
            <div className="col-sm-6 col-xl-3 mb-4">
              <div 
                className="card stat-card shadow-sm border-0" 
                style={{
                  background: "linear-gradient(135deg, #ff5858 0%, #f09819 100%)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  color: "white"
                }}
              >
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: "48px",
                        height: "48px",
                        background: "rgba(255, 255, 255, 0.2)"
                      }}>
                      <FaHandHoldingUsd className="fs-4" />
                    </div>
                    <div className="ms-auto">
                      <BsGraphUpArrow className="fs-5" style={{ opacity: 0.7 }} />
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <h6 className="mb-0 fw-semibold">Admin Today Earn</h6>
                    <span className="fw-bold fs-4">${data?.FirstUserTodayIncome?.toFixed(2) || 0}</span>
                  </div>
                  <div className="mt-2">
                    <small style={{ opacity: 0.8 }}>Today's earnings</small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BalanceCardSlider;